// src/services/authService.ts
import apiClient from './api'
import type { User } from '@/types'
import { useI18n } from '@/composables/useI18n'

export type TwoFactorChannel = 'email' | 'sms'

export interface TwoFactorChannelOption {
  channel: TwoFactorChannel
  destination: string
}

// What login() returns instead of a session when the account has two-step
// login on: the code went to `destination`, and `channels` lists where else
// it can be sent.
export interface TwoFactorChallenge {
  challengeId: string
  channel: TwoFactorChannel
  destination: string
  channels: TwoFactorChannelOption[]
  resendAfter: number
}

export interface AuthResponse {
  success: boolean
  token?: string
  user?: User
  data?: User
  message?: string
  error?: string
  requiresTwoFactor?: boolean
  challengeId?: string
  channel?: TwoFactorChannel
  destination?: string
  channels?: TwoFactorChannelOption[]
  resendAfter?: number
}

export interface TwoFactorStatus {
  enabled: boolean
  channel: TwoFactorChannel | null
  destination: string | null
  channels: TwoFactorChannelOption[]
}

export const authService = {
  async register(data: {
    fullName: string
    email: string
    phone: string
    password?: string
    city?: string
    role?: string
  }): Promise<AuthResponse> {
    const { currentLocale } = useI18n()
    const res = await apiClient.post<AuthResponse>('/api/auth/register', { ...data, locale: currentLocale.value?.code })
    return res.data
  },

  async login(credentials: {
    email: string
    password?: string
  }): Promise<AuthResponse> {
    const { currentLocale } = useI18n()
    const res = await apiClient.post<AuthResponse>('/api/auth/login', { ...credentials, locale: currentLocale.value?.code })
    return res.data
  },

  async verifyLoginCode(challengeId: string, code: string): Promise<AuthResponse> {
    const { currentLocale } = useI18n()
    const res = await apiClient.post<AuthResponse>('/api/auth/verify-login-otp', { challengeId, code, locale: currentLocale.value?.code })
    return res.data
  },

  async resendLoginCode(challengeId: string, channel?: TwoFactorChannel): Promise<{ success: boolean; channel: TwoFactorChannel; destination: string; resendAfter: number }> {
    const { currentLocale } = useI18n()
    const res = await apiClient.post('/api/auth/resend-login-code', { challengeId, channel, locale: currentLocale.value?.code })
    return res.data
  },

  async getTwoFactor(): Promise<TwoFactorStatus> {
    const res = await apiClient.get<{ success: boolean; data: TwoFactorStatus }>('/api/auth/2fa')
    return res.data.data
  },

  async startTwoFactorSetup(channel: TwoFactorChannel): Promise<{ challengeId: string; channel: TwoFactorChannel; destination: string; resendAfter: number }> {
    const res = await apiClient.post('/api/auth/2fa/setup', { channel })
    return res.data.data
  },

  async resendTwoFactorSetup(challengeId: string): Promise<{ resendAfter: number }> {
    const res = await apiClient.post('/api/auth/2fa/setup/resend', { challengeId })
    return res.data.data
  },

  async confirmTwoFactorSetup(challengeId: string, code: string): Promise<TwoFactorStatus> {
    const res = await apiClient.post('/api/auth/2fa/setup/confirm', { challengeId, code })
    return res.data.data
  },

  async disableTwoFactor(password: string): Promise<void> {
    await apiClient.post('/api/auth/2fa/disable', { password })
  },

  async getMe(): Promise<AuthResponse> {
    const res = await apiClient.get<AuthResponse>('/api/auth/me')
    return res.data
  },

  async logout(): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await apiClient.post<{ success: boolean; message?: string }>('/api/auth/logout')
      return res.data
    } catch {
      return { success: true }
    }
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const res = await apiClient.post<{ success: boolean; message?: string; error?: string }>('/api/auth/forgot-password', { email })
    return res.data
  },

  async resetPassword(token: string, password: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const res = await apiClient.post<{ success: boolean; message?: string; error?: string }>('/api/auth/reset-password', { token, password })
    return res.data
  },

  async verifyEmail(token: string): Promise<{ success: boolean; message?: string; error?: string; user?: User }> {
    const res = await apiClient.post<{ success: boolean; message?: string; error?: string; user?: User }>('/api/auth/verify-email', { token })
    return res.data
  },

  // Sends the verification link again to the signed-in user's address.
  async resendVerification(): Promise<{ success: boolean; message?: string; error?: string; emailSent?: boolean; alreadyVerified?: boolean }> {
    const res = await apiClient.post<{ success: boolean; message?: string; error?: string; emailSent?: boolean; alreadyVerified?: boolean }>('/api/auth/resend-verification', {})
    return res.data
  },
}

export default authService
